import request from 'sb-request'
import actions from 'redux/actions'
import { getState, reducers } from 'redux/core'
import { constants, fbq, getGqlQuery, links, mixpanel, sentry, getMixpanelSubscriptionStatus } from 'common/helpers'
import { modifySubscription } from 'common/helpers/modifiers/modifyResponse'
import { getFeatureToggle } from 'helpers'


const set = (subscription) => {
  const { isSubscribed, status } = subscription

  reducers.models.subscription.update(subscription)

  // resolve postponed LEP
  if (isSubscribed) {
    // Try to add postponed limited items for user after subscription
    actions.models.queue.resolveAllPostponedLimitedItems({ withoutErrorHandler: true })
  }

  // set cookie for gtm attentive popup
  actions.misc.cookies.setSessionItem(constants.cookieNames.pastPurchaser, isSubscribed ? 1 : 0)

  if (window.mixpanel) {
    const mixpanelStatus = getMixpanelSubscriptionStatus(status)

    window.mixpanel.register({
      'Subscription Status': mixpanelStatus,
    })
  }
}

/**
 * Fetches user's subscription
 *
 * @param {Promise} [awaitFor]
 */
const fetch = (awaitFor) =>
  request.get('rest/subscription', {
    multiple: true,
    modifyResult: modifySubscription,
  })
    .then((result) => {
      if (awaitFor) {
        awaitFor.then(() => { set(result) })
      }
      else {
        set(result)
      }

      return result
    })

const fetchPlans = () =>
  request.post('graphql?name=plans', {
    body: {
      query: `{
        plans {
          name
          monthCount: billingPeriod
          shippingPeriod
          productCount
          totalPrice
          productPrice
          selected
          scentCount: productsPerPeriod
        }
      }`,
    },
    modifyResult: ({ plans }) => {
      plans = plans || []

      const groupsByCount = []

      plans
        .filter((plan) => {
          if (plan && plan.scentCount) {
            return true
          }

          sentry.captureException(`model.subscription.actions.fetchPlans plan does not have required scentCount prop`, {
            extra: {
              plans,
            },
          })

          return false
        })
        .forEach((plan) => {
          const currrentGroup = groupsByCount.find(({ scentCount } = {}) => scentCount === plan.scentCount)

          if (!currrentGroup) {
            groupsByCount.push({
              scentCount: plan.scentCount,
              plans: [ plan ],
            })
          }
          else {
            currrentGroup.plans.push(plan)
          }
        })

      return groupsByCount
    },
  })
    .then((result) => {
      reducers.models.subscription.setPlans(result)

      return result
    })

/**
 *
 * @param {Object} data
 * @param {number} [data.products]
 * @param {number} [data.shipping]
 * @param {number} data.billing
 */
const fetchPlansSizes = ({ products, shipping, billing } = {}) =>
  request.post('graphql?name=plansQuery', {
    body: {
      query: `{
        plans: ${getGqlQuery('plansQuery', { products, shipping, billing })} {
          planName: name
          size: billingPeriod
          products: productsPerPeriod
          price
          save
        }
      }`,
    },
    modifyResult: ({ plans }) => plans || [],
  })

const fetchFrequencies = () =>
  request.get('rest/subscription/frequencies')

const fetchFrequenciesForResubscribeUser = () =>
  request.get('rest/resubscribe-reminder-popup/frequencies')

const fetchCurrentPlan = () =>
  request.get('rest/subscription/plan')
    .then((result) => reducers.models.subscription.setCurrentPlan(result))

const fetchCurrentPlanProductPrice = () => {
  const { models: { subscription: { currPlan } } } = getState()
  const { billingPeriod, shippingPeriod, productsPerPeriod } = currPlan

  if (!billingPeriod || !shippingPeriod || !productsPerPeriod) {
    return Promise.resolve()
  }

  const plansQuery = getGqlQuery('plansQuery', {
    products: productsPerPeriod,
    shipping: shippingPeriod,
    billing: billingPeriod,
  })

  return request.post('graphql?name=planQuery', {
    body: {
      query: `{
        plan: ${plansQuery} {
          productPrice
        }
      }`,
    },
    modifyResult: ({ plan }) => plan && plan[0] ? plan[0].productPrice : null,
  })
    .then((productPrice) => reducers.models.subscription.setCurrentPlan({ ...currPlan, productPrice }))
}

const updateStatus = (status) => reducers.models.subscription.update({ status })

const setUnsubscribeFeedbackSentDate = (unsubscribeFeedbackSentDate) => reducers.models.subscription.update({ unsubscribeFeedbackSentDate })

/**
 *
 * @param {String} planName
 * @param {String} placement
 */
const changePlan = (planName, placement) =>
  // TODO replace `plan` with `planName` or `name`
  request.post('rest/subscription/plan', {
    body: {
      plan: planName,
      placement,
    },
  })
    .then(() => {
      actions.models.queue.fetch()

      return fetch()
    }, ({ statusCode, resData }) => {
      if (statusCode !== 409) {
        return Promise.reject(resData)
      }

      actions.models.router.push(links.cardUpdate)

      return Promise.reject({ statusCode, resData })
    })

/**
 * Skip Months
 * @param count {Number}
 * @param limitedPause {Boolean}
 * @returns {Promise<any | never>}
 */
const skipMonths = ({ count, limitedPause = false }) =>
  // TODO remove camelCase, replace `month` with `count`
  request.post('rest/subscription/skip', {
    body: {
      month: count,
      limitedPause,
    },
  })
    .then((data) => {
      const { refund } = data || {}

      return fetch()
        .then(() => refund)
    })


// TODO: This FT is always on? Refine this and remove the logic.
const isUnskipFlowEnabled = getFeatureToggle(constants.features.unskipFlow)

/**
 *
 * @param {String} placement
 */
const unskip = (placement) =>
  request.post('rest/subscription/unskip', {
    body: {
      placement,
    },
    modifyResult: modifySubscription,
  })
    .then(
      (result = {}) => {
        const { notificationUserAboutPremiumProductInBatch, premiumProduct, status } = result

        // If the queue is locked notificationUserAboutPremiumProductInBatch: true
        // and server send first premiumProduct of current month, if he is exists.
        const showPremiumWelcomeBackModal  = notificationUserAboutPremiumProductInBatch && Boolean(premiumProduct)

        if (isUnskipFlowEnabled) {
          reducers.models.subscription.update(result)

          if (status === constants.subscriptionStatuses.unpaid) {
            actions.models.router.push(links.cardUpdate)
          }
          else {
            if (showPremiumWelcomeBackModal) {
              actions.models.modals.open(constants.modals.premiumProducts.WelcomeBack, {
                item: premiumProduct,
              })
            }
            else {
              actions.models.modals.open(constants.modals.UnskipSuccess)
            }

            actions.models.queue.fetch()
          }
        }
        else {
          if (showPremiumWelcomeBackModal) {
            actions.models.modals.open(constants.modals.premiumProducts.WelcomeBack, {
              item: premiumProduct,
            })
          }

          return fetch()
        }
      },
      ({ statusCode }) => {
        if (statusCode === 409 && isUnskipFlowEnabled) {
          return fetch()
            .then(({ isUnpaid }) => {
              if (isUnpaid) {
                actions.models.router.push(links.cardUpdate)
              }
            })
        }
      })

/**
 *
 * @param {Boolean} short - return 5 reasons for new flow
 * @returns {*}
 */
const fetchUnsubscribeReasons = (short) =>
  request.get('rest/subscription/unsubscribeReasons', {
    query: {
      short,
    },
  })

/**
 * @description: This method used in RefundModal. He have 2 btns: refund & receive.
 * Backend needs to know what the user has chosen.
 * constants: subscriptionRefundTypes
 * @param {Object} opts - one of 'RECEIVE' || 'REFUND'
 * @param {string} opts.decision - one of 'RECEIVE' || 'REFUND'
 * @param {string} opts.transactionType - one of 'PAUSE' || 'SKIP' || 'UNSUBSCRIBE'
 */
const refund = ({ decision, transactionType }) =>
  request.post('rest/subscription/refund', {
    body: {
      decision,
      transactionType,
    },
  })

/**
 * @param {Object} values
 * @param {String} values.reason - reason name
 * @param {String} [values.comment] - user input
 * @returns {Promise<any>}
 */
const unsubscribe = (values) => {
  actions.models.ui.showRequestLoader()

  return request.post('rest/subscription/unsubscribe', {
    body: values,
    tokenName: constants.requestTokens.unsubscribe,
  })
    .then((data) => {
      fetch()
      actions.view.ordersStatusPage.fetchItems()
      actions.models.queue.fetch()
      fbq.track('unsubscribe')
      reducers.models.subscription.setUnpaused(false)
      reducers.models.subscription.setJustCancelled(true)

      return data
    })
    .finally(() => actions.models.ui.hideRequestLoader())
}

const rateResubscribe = (rate) =>
  request.post('rest/subscription/unsubscribe/rate', {
    body: {
      resubscribeRate: rate,
    },
  })

/**
 * Resubscribe user
 *
 * @param {Object} data
 * @param {String} data.resubscribeCode
 * @param {String} data.popup
 * @param {String} data.plan
 * @param {Boolean} data.freeCase
 * @param {Boolean} data.withErrorHandler
 */
const resubscribe = (data = {}) => {
  const { resubscribeCode = '', popup, plan, freeCase, withErrorHandler = true } = data

  actions.models.ui.showRequestLoader()

  return request.post('rest/subscription/resubscribe', {
    body: {
      resubscribeCode,
      freeCase,
      plan,
      popup,
    },
    tokenName: constants.requestTokens.resubscribe,
    withErrorModal: false,
    modifyResult: ({ notificationUserAboutPremiumProductInBatch, premiumProduct, ...rest }) => ({
      ...rest,
      // If the queue is locked notificationUserAboutPremiumProductInBatch: true
      // and server send first premiumProduct of current month, if he is exists.
      premiumProduct: notificationUserAboutPremiumProductInBatch ? premiumProduct : null,
    }),
  })
    .then(
      (result = {}) => {
        if (plan) {
          actions.models.queue.fetch()
        }

        fetch()
        actions.view.ordersStatusPage.fetchItems()

        return result
      },
      ({ statusCode, error, resData, resData: { redirectUrl } }) => {
        let isRedirected = false

        if (Boolean(redirectUrl)) {
          isRedirected = true

          actions.models.cart.addSubscription()
            .then(() => actions.models.router.push(redirectUrl, true))
        }
        else if (error === 'subscribed') {
          if (plan) {
            actions.models.queue.fetch()
          }

          fetch()
          actions.view.ordersStatusPage.fetchItems()

          // resolve promise because user has been resubscribed already but has not updated subscription model for some reason
          return resData
        }
        else if (statusCode === 409 && withErrorHandler) {
          isRedirected = true

          actions.models.cart.addSubscription()
            .then(() => {
              const url = `${links.subscribe}?resubscribeCode=${resubscribeCode}`
              actions.models.router.push(url)
            })
        }

        return Promise.reject({ statusCode, error, isRedirected })
      }
    )
    .finally(() => actions.models.ui.hideRequestLoader())
}

const fetchPlanCosts = (planName) =>
  request.get('rest/subscription/plan/inquire', {
    query: {
      plan: planName,
    },
    // Case on the Thank you page if user will update subscription from navigation menu
    multiple: true,
  })
    .then((result) => {
      reducers.models.subscription.setPlanCosts(result)
      return result
    })

/**
 *
 * @param {String} planName
 */
const getPlanCost = (planName) =>
  request.get('rest/subscription/upgradeInquire', {
    query: {
      plan: planName,
    },
  })

/**
 *
 * @param {Object} values
 * @param {string} values.placement
 */
const handleUpgradeClick = (values) => {
  const { models: { subscription: { isUpgradable, isUpgradableForMonthlyPlan  } } } = getState()
  const { placement } = values

  const isAbUpgradePageParticipate = isUpgradableForMonthlyPlan // MONTHLY only
  const isUpgradeLandingPageEnabled = getFeatureToggle(constants.features.upgradeLandingPage)

  if (isAbUpgradePageParticipate) {
    mixpanel.track(constants.analyticsEvents.upgradeSimplePage.abStart)
  }

  if (isAbUpgradePageParticipate && isUpgradeLandingPageEnabled) {
    actions.models.router.push(links.upgradePlan)
  }
  else if (isUpgradable) {
    actions.models.modals.open(constants.modals.UpgradeSubscription, { placement })
  }
  else {
    actions.models.router.push(links.profile.upgradeSubscription)
  }
}

const upgradePlan = ({ size: plan, placement }) => {
  actions.models.ui.showRequestLoader()

  return request.post('rest/subscription/upgrade', {
    body: {
      plan,
      placement,
      token: constants.requestTokens.upgrade,
    },
    tokenName: 'upgrade',
  })
    .then((data) => {
      reducers.models.subscription.setCurrentPlan(data.plan)
      return data
    })
    .finally(() => actions.models.ui.hideRequestLoader())
}

const applyCoupon = (couponCode) =>
  request.post('rest/subscription/coupon', {
    body: {
      couponCode,
    },
    tokenName: constants.requestTokens.applyCouponForSubscription,
  })

const redeemSubscribed = (code) => {
  actions.models.ui.showRequestLoader()

  return request.post('rest/gift/subscription/redeemSubscribed', {
    body: {
      code,
    },
    tokenName: constants.requestTokens.redeemGiftSubscription,
  })
    .then((data) => {
      actions.models.queue.fetch()
      actions.models.modals.open(constants.modals.RedeemSuccess)

      reducers.models.me.activateGiftSubscription()

      return data
    })
    .finally(() => actions.models.ui.hideRequestLoader())
}

const fetchDataAfterPause = () => {
  fetch()
  actions.view.ordersStatusPage.fetchItems()
  actions.models.queue.fetch()
}

const pause = () =>
  request.post('rest/subscription/pause')
    .then((data) => {
      const { refund } = data || {}

      return refund
    })

/**
 * Unpauses subscription
 * discount may be unpauseType OR freeCase - not both
 *
 * @param {Object}  data
 * @param {String}  [data.unpauseType]
 * @param {Boolean} [data.freeCase]
 * @param {String}  data.placement
 * @param {Boolean} [data.isCancellationFlow]
 */
const unpause = (data = {}) => {
  const { unpauseType: unpauseCode, placement, isCancellationFlow, freeCase } = data

  actions.models.ui.showRequestLoader()

  return request.post('rest/subscription/unpause', {
    body: {
      unpauseCode,
      freeCase,
      placement,
    },
    tokenName: constants.requestTokens.unpause,
  })
    .then((result) => {
      const { notificationUserAboutPremiumProductInBatch, premiumProduct } = result

      if (isCancellationFlow) {
        reducers.models.subscription.setUnpaused(true)
      }
      else {
        // If the queue is locked notificationUserAboutPremiumProductInBatch: true
        // and server send first premiumProduct of current month, if he is exists.
        if (notificationUserAboutPremiumProductInBatch && premiumProduct) {
          actions.models.modals.open(constants.modals.premiumProducts.WelcomeBack, {
            item: premiumProduct,
          })
        }
        else {
          actions.models.modals.open(constants.modals.pauseSubscription.UnpauseSuccess, { unpauseCode, unpauseFreeCase: freeCase })
        }
      }

      fetchDataAfterPause()
    }, ({ statusCode, error }) => {
      if (statusCode !== 409 || error === 'unexpected_error') {
        return Promise.reject()
      }

      if (error === 'not_subscribed') {
        fetch()

        return Promise.reject()
      }

      actions.misc.cookies.setSessionItem(constants.cookieNames.unpauseData, data)
      actions.models.router.push(links.cardUpdate)
    })
    .finally(() => actions.models.ui.hideRequestLoader())
}

/**
 *
 * @param {Boolean} value
 */
const setDowngrade = (value) => reducers.models.subscription.setDowngrade(value)

const getDowngradePlan = async (state) => {
  let { models: { subscription: { downgradePlan, currPlan: { shippingPeriod } } } } = state || getState()

  if (downgradePlan && downgradePlan.planName) {
    return downgradePlan
  }

  try {
    const plans = await fetchPlansSizes({ products: 1, shipping: shippingPeriod })

    downgradePlan = plans.find(({ size }) => size === shippingPeriod) || {}
  }
  catch (err) {
    sentry.captureException(`subscription actions.js getDowngradePlan ${err}`)

    downgradePlan = {}
  }

  reducers.models.subscription.setDowngradePlan(downgradePlan)
  return downgradePlan
}

const downgrade = async () => {
  const { planName } = await getDowngradePlan()

  if (planName) {
    try {
      const response = await changePlan(planName, 'downgrade')

      if (response instanceof Error) {
        const error = (response && response.stack) ? response.stack : response
        sentry.captureException('Error in changePlan() + extra', {
          extra: {
            error,
          },
        })

        return Promise.reject(response)
      }

      mixpanel.track(constants.analyticsEvents.downgrade)
      setDowngrade(false)

      return response
    }
    catch (err) {
      const error = (err && err.stack) ? err.stack : err
      sentry.captureException('Error in changePlan() + extra', {
        extra: {
          error,
        },
      })

      return Promise.reject(err)
    }
  }
}


export default {
  fetch,
  fetchPlans,
  fetchPlansSizes,
  fetchFrequencies,
  fetchCurrentPlan,
  fetchCurrentPlanProductPrice,
  fetchFrequenciesForResubscribeUser,
  set,
  updateStatus,
  changePlan,
  skipMonths,
  unskip,
  fetchUnsubscribeReasons,
  unsubscribe,
  resubscribe,
  fetchPlanCosts,
  getPlanCost,
  handleUpgradeClick,
  upgradePlan,
  applyCoupon,
  refund,
  pause,
  unpause,
  downgrade,
  setDowngrade,
  getDowngradePlan,
  redeemSubscribed,
  rateResubscribe,
  fetchDataAfterPause,
  setUnsubscribeFeedbackSentDate,
}
